import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProject, getNearby, getAssessAll, startAssessAll, getAssessStatus, getAssessResult, getAssessCacheInfo } from '../api/client';
import { T } from '../assets/theme';
import { ConfirmModal } from '../components/UI';
import { useAuth } from '../assets/AuthContext';
import EditObstacleModal from '../components/EditObstacleModal';
import EditAerodromeModal from '../components/EditAerodromeModal';
import ObstacleContextMenu from '../components/ObstacleContextMenu';
import { computeHullObstacles, HULL_MODE_KEY } from '../utils/convexHull';

// ── Sub-pages ──────────────────────────────────────────────────────────────────
import ObstaclePreview from './assessment/ObstaclePreview';
import OLSPage from './assessment/OLSPage';
import OlsAdgPage from './assessment/OlsAdgPage';
import CNSBraPage from './assessment/CNSBraPage';
import PansOpsPage from './assessment/PansOpsPage';
import NpaPage from './assessment/NpaPage';
import VssPage from './assessment/VssPage';
import CirclingPage from './assessment/CirclingPage';
import DepartureOisPage from './assessment/DepartureOisPage';
import ArrivalOisPage from './assessment/ArrivalOisPage';
import MsaTaaPage from './assessment/MsaTaaPage';
import RnavRoutesPage from './assessment/RnavRoutesPage';
import AirspaceAssessmentPage from './assessment/AirspaceAssessmentPage';
import RmacPage from './assessment/RmacPage';
import ResultsPage from './assessment/ResultsPage';
import ReportPreview from './assessment/ReportPreview';
import QaSignoffPage from './assessment/QaSignoffPage';

// Helper: human-readable time ago from ISO string
function _timeAgo(isoStr) {
    try {
        const d = new Date(isoStr);
        const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch (e) { return ''; }
}

// perm: the permission key required; null = always visible
const TABS = [
    { key: 'preview',       icon: '🗺',  label: 'Obstacle Preview',              short: 'Preview',  perm: null },
    { key: 'ols',           icon: '✈',   label: 'Annex 14 OLS',                  short: 'OLS',      perm: 'ols' },
    { key: 'ols-adg',       icon: '🚧',  label: 'OLS (ADG)',                      short: 'OLS ADG',  perm: 'ols_adg' },
    { key: 'pans',          icon: '🛫',  label: 'Precision Approach',             short: 'PA',       perm: 'pa' },
    { key: 'npa',           icon: '🛬',  label: 'Non-Precision Approach',         short: 'NPA',      perm: 'npa' },
    { key: 'vss',           icon: '👁️', label: 'Visual Segment Surface',         short: 'VSS',      perm: 'vss' },
    { key: 'circling',      icon: '🔄',  label: 'VISUAL MANOEUVRING (Circling)', short: 'Circling', perm: 'circling' },
    { key: 'departure-ois', icon: '🛫',  label: 'Departure OIS',                  short: 'SID OIS',  perm: 'sid_ois' },
    { key: 'arrival-ois',   icon: '🛬',  label: 'Arrival OIS',                    short: 'STAR OIS', perm: 'star_ois' },
    { key: 'msataa',        icon: '🏔',  label: 'MSA',                            short: 'MSA',      perm: 'msa' },
    { key: 'rnav-routes',   icon: '🛣️', label: 'RNAV Routes',                    short: 'RNAV',     perm: 'rnav' },
    { key: 'airspace',      icon: '🛂',  label: 'Airspace',                       short: 'Airspace', perm: 'airspace' },
    { key: 'rmac',          icon: '🛬',  label: 'RMAC',                           short: 'RMAC',     perm: 'rmac' },
    { key: 'cns',           icon: '📡',  label: 'BRA CNS',                        short: 'CNS',      perm: 'cns' },
    { key: 'results',       icon: '📊',  label: 'Results',                        short: 'Results',  perm: null },
    { key: 'report',        icon: '📄',  label: 'Report Preview',                 short: 'Report',   perm: null },
    { key: 'qa',            icon: '🧾',  label: 'QA Sign-Off',                    short: 'QA',       perm: null },
];

/* ── Inject keyframes once ─────────────────────────────────────────────────── */
const SPINNER_ID = 'tsela-spinner-kf';
if (typeof document !== 'undefined') {
    let style = document.getElementById(SPINNER_ID);
    if (!style) {
        style = document.createElement('style');
        style.id = SPINNER_ID;
        document.head.appendChild(style);
    }
    style.textContent = `
        @keyframes tselaModalIn {
            from { opacity:0; transform:scale(.92) translateY(8px); }
            to   { opacity:1; transform:scale(1)   translateY(0);   }
        }
        
        .flight-container {
            position: relative;
            width: 80px;
            height: 80px;
            margin-bottom: 8px;
        }

        .flight-path {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            border: 2.5px dashed rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            animation: spinPath 12s linear infinite; 
        }

        .plane-wrapper {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            animation: spin 1.5s linear infinite; 
        }

        .airplane {
            position: absolute;
            top: -12px; 
            left: 50%;
            transform: translateX(-50%) rotate(90deg); 
        }

        .airplane svg {
            width: 24px;
            height: 24px;
            fill: #FFF;
            filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        @keyframes spinPath {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(-360deg); }
        }
    `;
}

/* ── Loading Modal — pure display, receives real progress & step ───────────── */
function LoadingModal({ progress, step, projectName }) {
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(6,14,26,0.82)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                background: 'linear-gradient(145deg, rgba(13,30,56,0.95), rgba(10,22,40,0.98))',
                border: `1px solid ${T.border}`,
                borderRadius: 16, padding: '36px 48px',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 16, minWidth: 360, maxWidth: 440,
                boxShadow: '0 16px 48px rgba(0,0,0,0.50), 0 0 0 1px rgba(58,138,196,0.10)',
                animation: 'tselaModalIn 0.3s ease-out',
            }}>
                {/* Custom Airplane Loader */}
                <div className="flight-container">
                    <div className="flight-path"></div>
                    <div className="plane-wrapper">
                        <div className="airplane">
                            <svg viewBox="0 0 24 24">
                                <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                            </svg>
                        </div>
                    </div>
                </div>
                
                {/* Project name */}
                {projectName && (
                    <div style={{
                        fontSize: 14, fontWeight: 700, color: T.white,
                        textAlign: 'center', letterSpacing: '0.02em',
                    }}>
                        {projectName}
                    </div>
                )}

                {/* Current step description */}
                <div style={{
                    fontSize: 13, color: T.blueLt, fontWeight: 500,
                    textAlign: 'center', minHeight: 20,
                }}>
                    {step}
                </div>

                {/* Progress bar */}
                <div style={{ width: '100%' }}>
                    {/* Percentage label */}
                    <div style={{
                        fontSize: 12, color: T.muted, fontWeight: 600,
                        textAlign: 'right', marginBottom: 6,
                    }}>
                        {progress}%
                    </div>

                    {/* Bar track */}
                    <div style={{
                        width: '100%', height: 8, borderRadius: 4,
                        background: 'rgba(58,138,196,0.12)',
                        overflow: 'hidden',
                    }}>
                        {/* Bar fill */}
                        <div style={{
                            height: '100%', borderRadius: 4,
                            width: `${progress}%`,
                            background: `linear-gradient(90deg, ${T.blue}, ${T.blueLt})`,
                            boxShadow: `0 0 12px ${T.blue}66`,
                            transition: 'width 0.4s ease-out',
                        }} />
                    </div>
                </div>
            </div>
        </div>
    );
}

const EMPTY_ARR = [];

export default function AssessmentPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();

    const visibleTabs = TABS.filter(t => t.perm === null || can(t.perm));

    // Allow deep-linking straight to a tab, e.g. /projects/:id/assess?tab=qa
    // from the "Continue QA Sign-Off" shortcut on the project page.
    const initialTab = new URLSearchParams(window.location.search).get('tab') || 'preview';
    const [tab, setTab] = useState(initialTab);
    const [project, setProject] = useState(null);
    const [nearby, setNearby] = useState({ nearby_aerodromes: [], nearby_cns: [] });
    const [assessAll, setAssessAll] = useState(null);
    // ── sessionStorage helpers ──────────────────────────────────────────────
    const OLS_CACHE_KEY = `tsela_ols_${id}`;
    function readOlsCache() {
        try { return JSON.parse(sessionStorage.getItem(OLS_CACHE_KEY) || 'null') || {}; } catch { return {}; }
    }
    function writeOlsCache(patch) {
        try {
            const prev = readOlsCache();
            sessionStorage.setItem(OLS_CACHE_KEY, JSON.stringify({ ...prev, ...patch }));
        } catch (e) {
            // sessionStorage quota exceeded — clear oldest entries
            try { sessionStorage.removeItem(OLS_CACHE_KEY); } catch {}
        }
    }

    const [olsSurfaceData, _setOlsSurfaceData] = useState(() => readOlsCache().surfaceData || {});
    const [olsVis,         _setOlsVis]         = useState(() => readOlsCache().vis         || {});
    const [olsExpanded,    _setOlsExpanded]    = useState(() => readOlsCache().expanded     || {});
    const [olsCaptured,    _setOlsCaptured]    = useState(() => readOlsCache().captured     || {});

    // Wrapped setters that also persist to sessionStorage
    const setOlsSurfaceData = (updater) => {
        _setOlsSurfaceData(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            writeOlsCache({ surfaceData: next });
            return next;
        });
    };
    const setOlsVis = (updater) => {
        _setOlsVis(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            writeOlsCache({ vis: next });
            return next;
        });
    };
    const setOlsExpanded = (updater) => {
        _setOlsExpanded(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            writeOlsCache({ expanded: next });
            return next;
        });
    };
    const setOlsCaptured = (updater) => {
        _setOlsCaptured(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            writeOlsCache({ captured: next });
            return next;
        });
    };

    const [cnsVis, setCnsVis] = useState({});
    const [cnsCaptured, setCnsCaptured] = useState({});

    // ── Departure OIS Preserved State ──
    const [depOisState, setDepOisState] = useState({
        visible: {},
        rwyVisible: {},
        depResults: {},
        capturedPics: {}
    });

    // ── Arrival OIS Preserved State ──
    const [arrOisState, setArrOisState] = useState({ starResult: null });

    // ── PANS-OPS Preserved State ──
    const [pansOpsState, setPansOpsState] = useState({
        visible: {},
        ilsIcaos: [],
        ilsData: {},
        ilsResults: {},
        visIls: {},
        capturedIls: {}
    });

    // ── NPA Preserved State ──
    const [npaState, setNpaState] = useState({
        // npaResults: { [icao]: { polygons, results, minima, thresholds, procedure_results } }
        npaResults: {},
        // visNpa: { [icao_rwypair]: bool }  — true = visible
        visNpa: {},
        // procData: { [icao]: procedure[] }  — procedure KMZ layer data
        procData: {},
        // procTreeVis: { [icao]: { [nodeId]: bool } }  — tree checkboxes
        procTreeVis: {},
        // capturedNpaRwy: { [icao_rwy]: bool }  — per-runway screenshot state
        capturedNpaRwy: {},
    });

    // ── VSS Preserved State ──
    const [vssState, setVssState] = useState({
        vssResults: {},
        visVss: {},
    });

    // ── RMAC Preserved State ──
    const [rmacState, setRmacState] = useState({
        visible: {},
        rmacList: [],
        rmacLayers: {},
        rmacResults: {},
        visibleSectors: {}, // Persistent sector visibility
    });

    // ── Circling Preserved State ──
    const [circlingState, setCirclingState] = useState({
        selObstId: null,
        visible: {},
        catVisible: {},
        activeIcao: null,
        cirResults: {},
        capturedCircling: {},
    });

    // ── MSA Preserved State ──
    const [showObstacles, setShowObstacles] = useState(true);
    const [obsContextMenu, setObsContextMenu] = useState(null); // {x, y, obstacle}
    const [editingObstacle, setEditingObstacle] = useState(null);
    const handleObstacleContextMenu = useCallback((obstacle, x, y) => setObsContextMenu({ obstacle, x, y }), []);

    // ── Aerodrome (ARP) editing — same right-click-to-edit pattern as
    // obstacles, but writes to the shared arp_table reference data by ICAO
    // rather than a project-scoped row. ──
    const [arpContextMenu, setArpContextMenu] = useState(null); // {x, y, obstacle: arp}
    const [editingAerodrome, setEditingAerodrome] = useState(null);
    const handleAerodromeContextMenu = useCallback((arp, x, y) => setArpContextMenu({ obstacle: arp, x, y }), []);

    // ── "Pick on map" coordinate capture — shared by the obstacle and
    // aerodrome edit modals. activeMapRef always points at whichever
    // sub-page's Leaflet map is currently mounted (each page reports in via
    // onMapReady); picking just attaches a one-shot click handler to it. ──
    const activeMapRef = useRef(null);
    const pickHandlerRef = useRef(null);
    const [pickingActive, setPickingActive] = useState(false);
    const [pickedCoord, setPickedCoord] = useState(null);

    const handleMapReady = useCallback((mapInstance) => { activeMapRef.current = mapInstance; }, []);

    const cancelPicking = useCallback(() => {
        if (pickHandlerRef.current && activeMapRef.current) {
            activeMapRef.current.off('click', pickHandlerRef.current);
        }
        pickHandlerRef.current = null;
        setPickingActive(false);
    }, []);

    const startPicking = useCallback(() => {
        const map = activeMapRef.current;
        if (!map) return;
        setPickedCoord(null);
        setPickingActive(true);
        const handler = (e) => {
            setPickedCoord({ lat: e.latlng.lat, lon: e.latlng.lng });
            map.off('click', handler);
            pickHandlerRef.current = null;
            setPickingActive(false);
        };
        pickHandlerRef.current = handler;
        map.on('click', handler);
    }, []);

    // OLS (ADG) results aren't part of the central assessAll response (it's
    // assessed per-aerodrome inside its own tab) — lifted here, keyed by
    // ICAO, purely so the Results tab can show it alongside everything else.
    const [olsAdgResults, setOlsAdgResults] = useState({});
    const handleOlsAdgResultsUpdated = useCallback((icao, rows) => setOlsAdgResults(p => ({ ...p, [icao]: rows })), []);
    const [showObsLabels, setShowObsLabels] = useState(true);
    const [showArpLabels, setShowArpLabels] = useState(true);

    const [msaState, setMsaState] = useState({
        selObstId: null,
        visible: {},
        msaResults: {},
    });

    // ── CNS Preserved State ──
    const [cnsState, setCnsState] = useState({ visible: {}, selObstId: null, mapMode: 'street' });

    // ── RNAV Routes Preserved State ──
    const [rnavRoutesState, setRnavRoutesState] = useState({
        procData: {},
        procTreeVis: {},
        visible: {},
        capturedRnav: {},
    });

    // ── Airspace Assessment Preserved State ──
    const [airspaceState, setAirspaceState] = useState({ airspaceResult: null });

    // ── Real progress state ──────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [loadProgress, setLoadProgress] = useState(0);
    const [loadStep, setLoadStep] = useState('');

    const [assLoading, setAssLoading] = useState(false);
    const [assProgress, setAssProgress] = useState(0);
    const [assStep, setAssStep] = useState('');

    // ── Cache state ─────────────────────────────────────────────────────────────
    const [cacheInfo, setCacheInfo] = useState(null);
    const pollRef = useRef(null);

    // ── Load core project + nearby data (sequential for real progress) ───────
    const load = useCallback(async () => {
        setLoading(true);
        setLoadProgress(0);
        setLoadStep('Loading project data…');
        try {
            // Step 1: Load project
            const pRes = await getProject(id);
            setProject(pRes.data);
            setLoadProgress(33);
            setLoadStep('Querying nearby aerodromes…');

            const startMs = Date.now();
            const interval = setInterval(() => {
                const elapsed = Date.now() - startMs;
                // Roughly aim to reach ~95% after 4 seconds
                setLoadProgress(p => {
                    if (p >= 95) return p;
                    const bump = Math.random() * 3 + 1;
                    return Math.min(95, Math.floor(p + bump));
                });

                if (elapsed > 1000 && elapsed < 2000) setLoadStep('Analyzing spatial data…');
                if (elapsed > 2000 && elapsed < 3500) setLoadStep('Cross-referencing obstacles…');
                if (elapsed > 3500) setLoadStep('Compiling results…');
            }, 300);

            // Step 2: Load nearby (this is the slower call)
            try {
                const nRes = await getNearby(id, 102.0);
                setNearby(nRes.data);
            } finally {
                clearInterval(interval);
            }

            setLoadProgress(100);
            setLoadStep('Complete');
        } finally {
            // Brief pause so user sees 100% before modal closes
            await new Promise(r => setTimeout(r, 350));
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // ── Reset all per-project assessment state when the project changes ───────
    useEffect(() => {
        setRmacState({ visible: {}, rmacList: [], rmacLayers: {}, rmacResults: {}, visibleSectors: {} });
        setNpaState({ npaResults: {}, visNpa: {}, procData: {}, procTreeVis: {}, capturedNpaRwy: {} });
        setVssState({ vssResults: {}, visVss: {} });
        setPansOpsState({ visible: {}, ilsIcaos: [], ilsData: {}, ilsResults: {}, visIls: {}, capturedIls: {} });
        setDepOisState({ visible: {}, rwyVisible: {}, depResults: {}, capturedPics: {} });
        setRnavRoutesState({ procData: {}, procTreeVis: {}, visible: {}, capturedRnav: {} });
        setAirspaceState({ airspaceResult: null });
        // OLS: only clear sessionStorage for OTHER projects; keep current project cache intact.
        // (The useState initialisers already restored the correct cache on mount.)
        // Clear caches for any OTHER project IDs that may still be in sessionStorage (optional housekeeping).
        Object.keys(sessionStorage)
            .filter(k => k.startsWith('tsela_ols_') && k !== `tsela_ols_${id}`)
            .forEach(k => sessionStorage.removeItem(k));
        setCnsVis({});
        setCnsCaptured({});
        setAssessAll(null);
        setCirclingState({ selObstId: null, visible: {}, catVisible: {}, activeIcao: null, cirResults: {}, capturedCircling: {} });
        setCnsState({ visible: {}, selObstId: null, mapMode: 'street' });
        setMsaState({ selObstId: null, visible: {}, msaResults: {} });
    }, [id]); // runs whenever the project ID changes


    // ── Load assess-all (lazy, on tab switch to results/report or explicit) ──
    const loadAssessAll = useCallback(async (force = false) => {
        if (assLoading || !project) return;
        setAssLoading(true);
        setAssProgress(0);
        setAssStep('Starting assessment…');

        try {
            const r = await startAssessAll(id, 102.0, force);

            if (r.data.cached) {
                // Cache hit — instant result
                setAssessAll(r.data.result);
                setAssProgress(100);
                setAssStep('Loaded from cache');
                // Refresh cache info
                getAssessCacheInfo(id).then(ci => setCacheInfo(ci.data)).catch(() => {});
                await new Promise(r => setTimeout(r, 200));
                setAssLoading(false);
                return;
            }

            // Background job — poll for real progress
            const jobId = r.data.job_id;
            pollRef.current = setInterval(async () => {
                try {
                    const s = await getAssessStatus(id, jobId);
                    setAssProgress(s.data.progress);
                    setAssStep(s.data.step);

                    if (s.data.status === 'done') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        const result = await getAssessResult(id, jobId);
                        setAssessAll(result.data);
                        setAssProgress(100);
                        setAssStep('Assessment complete');
                        // Refresh cache info
                        getAssessCacheInfo(id).then(ci => setCacheInfo(ci.data)).catch(() => {});
                        await new Promise(r => setTimeout(r, 350));
                        setAssLoading(false);
                    } else if (s.data.status === 'error') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        setAssStep(`Error: ${s.data.error || 'Unknown error'}`);
                        await new Promise(r => setTimeout(r, 2000));
                        setAssLoading(false);
                    }
                } catch (err) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setAssStep('Connection lost');
                    setAssLoading(false);
                }
            }, 1500);
        } catch (err) {
            setAssStep(`Error: ${err.message}`);
            await new Promise(r => setTimeout(r, 2000));
            setAssLoading(false);
        }
    }, [id, project, assLoading]);

    // Clean up polling on unmount
    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    // Load cache info on project load
    useEffect(() => {
        if (id && !loading) {
            getAssessCacheInfo(id).then(r => setCacheInfo(r.data)).catch(() => {});
        }
    }, [id, loading]);

    useEffect(() => {
        if ((tab === 'results' || tab === 'report') && !assessAll && !assLoading) {
            loadAssessAll();
        }
    }, [tab]); // eslint-disable-line

    const rawObstacles = project?.obstacles || EMPTY_ARR;
    const hullMode = localStorage.getItem(HULL_MODE_KEY(project?.id)) === 'true';
    const obstacles = useMemo(
        () => hullMode ? computeHullObstacles(rawObstacles) : rawObstacles,
        [hullMode, rawObstacles]
    );
    const arps = nearby.nearby_aerodromes || EMPTY_ARR;
    const cns = nearby.nearby_cns || EMPTY_ARR;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

            {/* ── Loading modals (driven by real progress) ── */}
            {loading && (
                <LoadingModal
                    progress={loadProgress}
                    step={loadStep}
                    projectName={project?.name}
                />
            )}
            {assLoading && !loading && (
                <LoadingModal
                    progress={assProgress}
                    step={assStep}
                    projectName={project?.name}
                />
            )}

            {/* ── Header bar ── */}
            <div style={{
                background: T.bg1, borderBottom: `1px solid ${T.border}`,
                padding: '0 20px', display: 'flex', alignItems: 'center', gap: 16,
                minHeight: 48, flexShrink: 0,
            }}>
                <button
                    onClick={() => navigate(`/projects/${id}`)}
                    style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 12, padding: 0 }}
                >← Back</button>

                <span style={{ fontSize: 14, fontWeight: 800, color: T.white }}>
                    {project?.name || '…'}
                </span>
                <span style={{ fontSize: 11, color: T.muted }}>
                    ⚑ {obstacles.length} obstacle{obstacles.length !== 1 ? 's' : ''}
                    {hullMode && rawObstacles.length !== obstacles.length && (
                        <span style={{ marginLeft: 6, color: T.safe, fontWeight: 700 }}>
                            ⬡ Hull ({rawObstacles.length} → {obstacles.length} pts)
                        </span>
                    )}
                </span>

                {/* spacer */}
                <div style={{ flex: 1 }} />

                {/* Global Layer Toggles */}
                <div style={{ display: 'flex', gap: 16, background: 'rgba(255,255,255,0.03)', padding: '4px 12px', borderRadius: 6, border: `1px solid ${T.border}33` }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: showObstacles ? T.white : T.muted, cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={showObstacles} onChange={e => setShowObstacles(e.target.checked)} style={{ accentColor: T.blue, cursor: 'pointer' }} />
                        Obstacles
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: showObsLabels ? T.white : T.muted, cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={showObsLabels} onChange={e => setShowObsLabels(e.target.checked)} style={{ accentColor: T.blue, cursor: 'pointer' }} />
                        Obstacle Names
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: showArpLabels ? T.white : T.muted, cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={showArpLabels} onChange={e => setShowArpLabels(e.target.checked)} style={{ accentColor: T.blue, cursor: 'pointer' }} />
                        Aerodrome Names
                    </label>
                </div>

                {/* Refresh assess-all */}
                {(() => {
                    const ci = cacheInfo;
                    const isCurrent = ci?.cached && !ci?.stale;
                    const isStale = ci?.cached && ci?.stale;
                    const neverRun = !ci?.cached;

                    let label, bg, border, color;
                    if (assLoading) {
                        label = '⟳ Running…';
                        bg = 'rgba(46,201,139,0.08)';
                        border = `1px solid ${T.safe}44`;
                        color = T.safe;
                    } else if (isCurrent) {
                        const ago = ci?.run_at ? _timeAgo(ci.run_at) : '';
                        label = `✓ Results current${ago ? ` · ${ago}` : ''}`;
                        bg = 'rgba(46,201,139,0.10)';
                        border = `1px solid ${T.safe}55`;
                        color = T.safe;
                    } else if (isStale) {
                        label = '⚠ Stale — re-run needed';
                        bg = 'rgba(245,166,35,0.12)';
                        border = '1px solid rgba(245,166,35,0.55)';
                        color = '#f5a623';
                    } else {
                        label = '▶ Run Assessment';
                        bg = 'rgba(46,201,139,0.15)';
                        border = `1px solid ${T.safe}44`;
                        color = T.safe;
                    }

                    return (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <button
                                onClick={() => loadAssessAll(isStale || neverRun)}
                                disabled={assLoading || loading}
                                title={isCurrent ? 'Assessment results are current — click to load' : isStale ? 'Data has changed since last run — click to re-assess' : 'Run comprehensive assessment'}
                                style={{
                                    background: bg, border, borderRadius: 6,
                                    color, fontSize: 11, fontWeight: 600,
                                    padding: '4px 12px', cursor: assLoading ? 'default' : 'pointer',
                                }}
                            >
                                {label}
                            </button>
                            {isCurrent && (
                                <button
                                    onClick={() => loadAssessAll(true)}
                                    disabled={assLoading || loading}
                                    title="Force re-run assessment from scratch"
                                    style={{
                                        background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.border}33`,
                                        borderRadius: 6, color: T.muted, fontSize: 11, fontWeight: 500,
                                        padding: '4px 8px', cursor: 'pointer',
                                    }}
                                >
                                    ↻ Re-run
                                </button>
                            )}
                        </div>
                    );
                })()}
            </div>

            {/* ── Tab bar ── */}
            <div style={{
                display: 'flex', background: T.bg1,
                borderBottom: `1px solid ${T.border}`, flexShrink: 0,
                overflowX: 'auto',
            }}>
                {visibleTabs.filter(t => {
                    if (project?.project_type === 'annex_14_ols_assessment') {
                        return ['preview', 'ols', 'ols-adg', 'results', 'report', 'qa'].includes(t.key);
                    }
                    return true;
                }).map(t => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        title={t.label}
                        style={{
                            padding: '10px 14px', background: 'none', border: 'none',
                            borderBottom: tab === t.key ? `2px solid ${T.blue}` : '2px solid transparent',
                            color: tab === t.key ? T.blue : T.muted,
                            fontSize: 12, fontWeight: tab === t.key ? 700 : 400,
                            cursor: 'pointer', whiteSpace: 'nowrap',
                            transition: 'all 0.15s',
                        }}
                    >
                        {t.icon} {t.short || t.label}
                    </button>
                ))}
            </div>

            {/* ── Active sub-page ── */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {tab === 'preview' && (
                    <ObstaclePreview
                        project={project} nearby={nearby}
                        loading={loading}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'ols' && (
                    <OLSPage
                        project={project} arps={arps} obstacles={obstacles}
                        olsResults={assessAll?.ols_results || []}
                        loading={loading}
                        surfaceData={olsSurfaceData} setSurfaceData={setOlsSurfaceData}
                        vis={olsVis} setVis={setOlsVis}
                        expanded={olsExpanded} setExpanded={setOlsExpanded}
                        capturedIcaos={olsCaptured} setCapturedIcaos={setOlsCaptured}
                        onAssess={loadAssessAll}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'ols-adg' && <OlsAdgPage project={project} arps={arps} obstacles={obstacles} showObstacles={showObstacles} showObsLabels={showObsLabels} showArpLabels={showArpLabels} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} onResultsUpdated={handleOlsAdgResultsUpdated} />}
                {tab === 'pans' && (
                    <PansOpsPage
                        project={project} arps={arps} obstacles={obstacles}
                        loading={loading}
                        pansState={pansOpsState} setPansState={setPansOpsState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'npa' && (
                    <NpaPage
                        project={project} arps={arps} obstacles={obstacles}
                        loading={loading}
                        npaState={npaState} setNpaState={setNpaState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'vss' && (
                    <VssPage
                        project={project} arps={arps} obstacles={obstacles}
                        loading={loading}
                        vssState={vssState} setVssState={setVssState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'circling' && (
                    <CirclingPage
                        project={project} arps={arps} obstacles={obstacles}
                        loading={loading}
                        state={circlingState} setState={setCirclingState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'departure-ois' && (
                    <DepartureOisPage
                        project={project} arps={arps} obstacles={obstacles}
                        loading={loading}
                        oisState={depOisState} setOisState={setDepOisState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'arrival-ois' && (
                    <ArrivalOisPage
                        project={project} obstacles={obstacles}
                        loading={loading}
                        state={arrOisState} setState={setArrOisState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} showObsLabels={showObsLabels}
                    />
                )}
                {tab === 'msataa' && (
                    <MsaTaaPage
                        project={project}
                        arps={arps} obstacles={obstacles}
                        loading={loading}
                        state={msaState} setState={setMsaState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'rnav-routes' && (
                    <RnavRoutesPage
                        project={project}
                        arps={arps} obstacles={obstacles}
                        loading={loading}
                        state={rnavRoutesState} setState={setRnavRoutesState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'airspace' && (
                    <AirspaceAssessmentPage
                        project={project}
                        obstacles={obstacles}
                        loading={loading}
                        state={airspaceState} setState={setAirspaceState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} showObsLabels={showObsLabels}
                    />
                )}
                {tab === 'rmac' && (
                    <RmacPage
                        project={project} arps={arps} obstacles={obstacles}
                        loading={loading}
                        rmacState={rmacState} setRmacState={setRmacState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'cns' && (
                    <CNSBraPage
                        project={project} arps={arps} cns={cns} obstacles={obstacles}
                        loading={loading}
                        state={cnsState} setState={setCnsState}
                        showObstacles={showObstacles} onObstacleContextMenu={handleObstacleContextMenu} onMapReady={handleMapReady} onAerodromeContextMenu={handleAerodromeContextMenu} showObsLabels={showObsLabels} showArpLabels={showArpLabels}
                    />
                )}
                {tab === 'results' && (
                    <ResultsPage
                        project={project} assessAll={assessAll}
                        loading={assLoading} onRun={loadAssessAll}
                        depOisResults={depOisState.depResults}
                        arrOisResult={arrOisState.starResult}
                        airspaceResult={airspaceState.airspaceResult}
                        olsAdgResults={olsAdgResults}
                    />
                )}
                {tab === 'report' && (
                    <ReportPreview
                        project={project} assessAll={assessAll}
                        loading={assLoading} onRun={loadAssessAll}
                        npaResults={npaState?.npaResults || {}}
                        vssResults={vssState?.vssResults || {}}
                        rnavResults={rnavRoutesState?.rnavResults || {}}
                    />
                )}
                {tab === 'qa' && <QaSignoffPage project={project} />}
            </div>

            {obsContextMenu && (
                <ObstacleContextMenu
                    x={obsContextMenu.x} y={obsContextMenu.y} obstacle={obsContextMenu.obstacle}
                    onEdit={(o) => { setEditingObstacle(o); setObsContextMenu(null); }}
                    onClose={() => setObsContextMenu(null)}
                />
            )}
            {editingObstacle && (
                <EditObstacleModal
                    obstacle={editingObstacle}
                    compact
                    hidden={pickingActive}
                    pickedCoord={pickedCoord}
                    onRequestPickCoord={startPicking}
                    onClose={() => { setEditingObstacle(null); setPickedCoord(null); cancelPicking(); }}
                    onUpdated={updatedObs => setProject(p => ({
                        ...p,
                        obstacles: p.obstacles.map(o => o.id === updatedObs.id ? updatedObs : o),
                    }))}
                />
            )}

            {arpContextMenu && (
                <ObstacleContextMenu
                    x={arpContextMenu.x} y={arpContextMenu.y} obstacle={arpContextMenu.obstacle}
                    actionLabel="Edit Aerodrome"
                    onEdit={(a) => { setEditingAerodrome(a); setArpContextMenu(null); }}
                    onClose={() => setArpContextMenu(null)}
                />
            )}
            {editingAerodrome && (
                <EditAerodromeModal
                    aerodrome={editingAerodrome}
                    hidden={pickingActive}
                    onClose={() => setEditingAerodrome(null)}
                    onUpdated={updatedArp => setNearby(p => ({
                        ...p,
                        nearby_aerodromes: (p.nearby_aerodromes || []).map(a => a.icao === updatedArp.icao ? updatedArp : a),
                    }))}
                />
            )}

            {pickingActive && (
                <div style={{
                    position: 'fixed', top: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 2500,
                    background: T.blue, color: '#fff', padding: '8px 18px', borderRadius: 20,
                    fontSize: 12, fontWeight: 700, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    📍 Click anywhere on the map to set the location
                    <button onClick={cancelPicking} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>Cancel</button>
                </div>
            )}
        </div>
    );
}

